import React, { useCallback, useEffect, useState } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Stack,
  TextField,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { CardMembership, CheckCircle, CalendarToday, WarningAmber } from '@mui/icons-material';
import { format } from 'date-fns';
import { ro } from 'date-fns/locale';
import { useAuth } from '@contexts/AuthContext';
import subscriptionService, {
  type ClinicSubscriptionDto,
  type ClinicEntitlementsDto,
  type SubscriptionPlan,
} from '@services/subscriptionService';

function formatIso(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return format(d, 'dd MMM yyyy', { locale: ro });
}

function statusLabel(status: string, cancelAtPeriodEnd: boolean): string {
  if (cancelAtPeriodEnd && status === 'active') return 'Activ (anulare la sfarsitul perioadei)';
  switch (status) {
    case 'trialing':
      return 'Perioada de proba';
    case 'active':
      return 'Activ';
    case 'past_due':
      return 'Plata restanta';
    case 'cancelled':
      return 'Anulat';
    case 'expired':
      return 'Expirat';
    case 'pending':
      return 'Plata in asteptare (pending)';
    default:
      return status;
  }
}

function statusColor(status: string, cancelAtPeriodEnd: boolean): 'success' | 'warning' | 'error' | 'default' | 'info' {
  if (cancelAtPeriodEnd) return 'warning';
  if (status === 'pending') return 'info';
  if (status === 'active' || status === 'trialing') return 'success';
  if (status === 'past_due') return 'warning';
  if (status === 'cancelled' || status === 'expired') return 'error';
  return 'default';
}

function subscriptionPeriodEnd(sub: ClinicSubscriptionDto): Date | null {
  const raw = sub.currentPeriodEnd || sub.endDate;
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

const Subscriptions: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [current, setCurrent] = useState<ClinicSubscriptionDto | null>(null);
  const [entitlements, setEntitlements] = useState<ClinicEntitlementsDto | null>(null);
  const [history, setHistory] = useState<{
    subscriptions: ClinicSubscriptionDto[];
    events: Array<{ id: string; eventType: string; createdAt: string | null; payload: unknown }>;
  } | null>(null);
  const [info, setInfo] = useState('');
  const [planDialog, setPlanDialog] = useState<'subscribe' | 'change' | null>(null);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [effective, setEffective] = useState<'immediate' | 'period_end'>('immediate');
  const [cancelDialog, setCancelDialog] = useState(false);
  const [cancelAtEnd, setCancelAtEnd] = useState(true);
  const [cancelReason, setCancelReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [billingSubscribeMode, setBillingSubscribeMode] = useState<'manual' | 'online'>('online');

  const load = useCallback(async () => {
    if (user?.role !== 'clinic_admin') {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    setInfo('');
    try {
      const [p, cur, ent, hist] = await Promise.all([
        subscriptionService.getPlans('clinic'),
        subscriptionService.getClinicCurrent(),
        subscriptionService.getClinicEntitlements(),
        subscriptionService.getClinicHistory(),
      ]);
      setPlans(p);
      setCurrent(cur.subscription);
      setEntitlements(ent);
      setHistory(hist);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Nu am putut incarca abonamentul.');
    } finally {
      setLoading(false);
    }
  }, [user?.role]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (user?.role !== 'clinic_admin') return;
    const params = new URLSearchParams(window.location.search);
    const stripe = params.get('stripe');
    const checkoutLegacy = params.get('checkout');
    const success = stripe === 'success' || checkoutLegacy === 'success';
    const cancel = stripe === 'cancel' || checkoutLegacy === 'cancel';
    const portalReturn = stripe === 'portal-return';
    if (success) {
      setInfo('Revenire din Stripe — actualizam statusul dupa confirmarea platii (webhook).');
      window.history.replaceState({}, '', window.location.pathname);
      void load();
    } else if (cancel) {
      setError('Checkout anulat — abonamentul nu s-a activat pana la plata.');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (portalReturn) {
      setInfo('Revenire din Customer Portal — reincarcam abonamentul si istoricul (modificarile pot fi confirmate si prin webhook).');
      window.history.replaceState({}, '', window.location.pathname);
      void load();
    }
  }, [user?.role, load]);

  if (user?.role !== 'clinic_admin') {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="info">Aceasta pagina este disponibila pentru administratorii de clinica.</Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container sx={{ py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  const activeLike =
    current && ['active', 'trialing', 'past_due'].includes(current.status);
  const pendingLike = current?.status === 'pending';

  const periodEnd = current ? subscriptionPeriodEnd(current) : null;
  const stillInPaidPeriod = periodEnd != null && periodEnd.getTime() > Date.now();

  /** Abonament Stripe activ: schimbare plan / anulare / reactivare doar prin Customer Portal. */
  const isOnlineStripeManaged =
    current?.billingMode === 'online' &&
    Boolean(current.stripeSubscriptionId) &&
    current != null &&
    ['active', 'trialing', 'past_due'].includes(current.status);

  const canSubscribe = !activeLike && !pendingLike && plans.filter((p) => p.isActive).length > 0;
  const canChange =
    activeLike && !pendingLike && !isOnlineStripeManaged && plans.filter((p) => p.isActive).length > 0;
  const canCancel = activeLike && !isOnlineStripeManaged;
  const canReactivate =
    current &&
    !isOnlineStripeManaged &&
    ((current.status === 'cancelled' && stillInPaidPeriod) ||
      (current.status === 'active' && current.cancelAtPeriodEnd));
  const canOpenStripePortal = Boolean(isOnlineStripeManaged);

  const activePlans = plans.filter((p) => p.isActive);

  const openSubscribe = () => {
    const first = activePlans[0];
    setSelectedPlanId(first?.id || '');
    setBillingSubscribeMode(
      first && (Number(first.price) === 0 || first.allowManualClinicActivation) ? 'manual' : 'online'
    );
    setPlanDialog('subscribe');
  };

  const openChange = () => {
    setSelectedPlanId(activePlans.find((p) => p.id !== current?.planId)?.id || activePlans[0]?.id || '');
    setEffective('immediate');
    setPlanDialog('change');
  };

  const runSubscribe = async () => {
    if (!selectedPlanId) return;
    setBusy(true);
    setError('');
    try {
      /** URL-uri success/cancel: pe server din STRIPE_SUCCESS_URL / STRIPE_CANCEL_URL (ex. healthypaws.ro); local din FRONTEND_URL. */
      const result = await subscriptionService.subscribeClinic(selectedPlanId, billingSubscribeMode);
      if ('checkoutUrl' in result && result.checkoutUrl) {
        setPlanDialog(null);
        window.location.assign(result.checkoutUrl);
        return;
      }
      setCurrent(result.subscription);
      setInfo('Abonament activat.');
      setPlanDialog(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Eroare');
    } finally {
      setBusy(false);
    }
  };

  const runOpenStripePortal = async () => {
    setBusy(true);
    setError('');
    setInfo('');
    try {
      const { portalUrl } = await subscriptionService.openStripeCustomerPortal();
      window.location.assign(portalUrl);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Nu s-a putut deschide Customer Portal.');
    } finally {
      setBusy(false);
    }
  };

  const runChange = async () => {
    if (!selectedPlanId) return;
    setBusy(true);
    setError('');
    try {
      const r = await subscriptionService.changeClinicPlan(selectedPlanId, effective);
      setCurrent(r.subscription);
      setInfo(
        r.scheduledChange
          ? 'Schimbarea de plan este programata la sfarsitul perioadei curente.'
          : 'Plan actualizat.'
      );
      setPlanDialog(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Eroare');
    } finally {
      setBusy(false);
    }
  };

  const runCancel = async () => {
    setBusy(true);
    setError('');
    try {
      const sub = await subscriptionService.cancelClinicSubscription(cancelAtEnd, cancelReason || undefined);
      setCurrent(sub);
      setInfo(cancelAtEnd ? 'Anulare programata la sfarsitul perioadei.' : 'Abonament anulat.');
      setCancelDialog(false);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Eroare');
    } finally {
      setBusy(false);
    }
  };

  const runReactivate = async () => {
    setBusy(true);
    setError('');
    try {
      const sub = await subscriptionService.reactivateClinicSubscription();
      setCurrent(sub);
      setInfo('Abonament reactivat / anulare programata anulata.');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Eroare');
    } finally {
      setBusy(false);
    }
  };

  const displayPlan = current?.plan;
  const displayName = displayPlan?.name || current?.planName || 'Fara plan activ';

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 3, fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
        Abonament clinica
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      ) : null}
      {info ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo('')}>
          {info}
        </Alert>
      ) : null}

      <Alert severity="info" sx={{ mb: 3 }} icon={<WarningAmber />}>
        <strong>Plata online</strong> foloseste Stripe Checkout (test/live dupa <code>STRIPE_SECRET_KEY</code>, preturi din plan sau{' '}
        <code>STRIPE_PRICE_BASIC</code> / <code>STRIPE_PRICE_PRO</code> / <code>STRIPE_PRICE_ENTERPRISE</code>, webhook{' '}
        <code>/api/v1/subscriptions/stripe/webhook</code>). <strong>Customer Portal</strong> („Gestioneaza abonamentul”) pentru schimbare plan, facturi si metoda de plata — fara succes fals; revenirea actualizeaza datele din server.
        <strong> Activarea manuala</strong> pentru planuri platite este permisa doar cu acord explicit pe server (trial/gratuit, plan dedicat, sau mediu
        dev/test); in productie veti folosi online sau Master Admin.
      </Alert>

      {activePlans.length > 0 ? (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Planuri disponibile
            </Typography>
            <Grid container spacing={2}>
              {activePlans.map((p) => (
                <Grid size={{ xs: 12, md: 6 }} key={p.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {p.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {p.description || '—'}
                      </Typography>
                      <Typography variant="h6" color="primary">
                        {p.price} {p.currency}
                        <Typography component="span" variant="caption" color="text.secondary">
                          {' '}
                          / {p.billingCycle}
                        </Typography>
                      </Typography>
                      <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
                        <Chip size="small" label={`Utilizatori: ${p.maxUsers ?? '—'}`} />
                        <Chip size="small" label={`Medici: ${p.maxDoctors ?? '—'}`} />
                        <Chip size="small" label={`Pacienti: ${p.maxAnimals ?? '—'}`} />
                        <Chip size="small" label={`Programari: ${p.maxAppointments ?? '—'}`} />
                      </Stack>
                      {p.features?.length ? (
                        <List dense sx={{ mt: 1 }}>
                          {p.features.slice(0, 6).map((f, i) => (
                            <ListItem key={i} sx={{ py: 0 }}>
                              <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={f} />
                            </ListItem>
                          ))}
                        </List>
                      ) : null}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      ) : null}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2, flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                <CardMembership sx={{ fontSize: 32 }} />
              </Avatar>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  {displayName}
                </Typography>
                {current ? (
                  <Chip
                    icon={<CheckCircle />}
                    label={statusLabel(current.status, current.cancelAtPeriodEnd)}
                    color={statusColor(current.status, current.cancelAtPeriodEnd)}
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                ) : (
                  <Chip label="Fara abonament activ" size="small" sx={{ mt: 0.5 }} />
                )}
              </Box>
            </Box>
            {current && Number(current.amount) > 0 ? (
              <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                <Typography variant="h4" color="primary.main" sx={{ fontWeight: 700 }}>
                  {current.amount} {current.currency}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  per ciclu facturare ({displayPlan?.billingCycle || '—'})
                </Typography>
              </Box>
            ) : null}
          </Box>

          {current ? (
            <>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Inceput perioada
                  </Typography>
                  <Typography variant="body1">{formatIso(current.currentPeriodStart || current.startDate)}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Sfarsit perioada
                  </Typography>
                  <Typography variant="body1">{formatIso(current.currentPeriodEnd || current.endDate)}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Urmatoarea facturare
                  </Typography>
                  <Typography variant="body1">{formatIso(current.nextBillingDate)}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Mod facturare
                  </Typography>
                  <Typography variant="body1">{current.billingMode}</Typography>
                </Grid>
              </Grid>
            </>
          ) : null}

          {entitlements?.limits ? (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Limite plan
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                <Chip label={`Utilizatori: ${entitlements.limits.maxUsers}`} size="small" />
                <Chip label={`Medici: ${entitlements.limits.maxDoctors}`} size="small" />
                <Chip label={`Pacienti (animale): ${entitlements.limits.maxPatients}`} size="small" />
                <Chip label={`Programari: ${entitlements.limits.maxAppointments}`} size="small" />
                <Chip
                  label={entitlements.premiumCapable ? 'Functionalitati premium: da' : 'Functionalitati premium: limitat'}
                  color={entitlements.premiumCapable ? 'success' : 'default'}
                  size="small"
                />
              </Stack>
            </>
          ) : null}

          {displayPlan?.features?.length ? (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Incluziuni (din plan)
              </Typography>
              <List dense>
                {displayPlan.features.map((feature, idx) => (
                  <ListItem key={idx} sx={{ py: 0.25 }}>
                    <Avatar sx={{ bgcolor: 'success.main', width: 22, height: 22, mr: 1 }}>
                      <CheckCircle sx={{ fontSize: 14 }} />
                    </Avatar>
                    <ListItemText primary={<Typography variant="body2">{feature}</Typography>} />
                  </ListItem>
                ))}
              </List>
            </>
          ) : null}

          {entitlements?.featureCodes?.length ? (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Coduri functionalitati (granulare)
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                {entitlements.featureCodes.slice(0, 12).join(', ')}
                {entitlements.featureCodes.length > 12 ? '…' : ''}
              </Typography>
            </>
          ) : null}

          {current?.nextBillingDate ? (
            <>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarToday color="action" fontSize="small" />
                <Typography variant="body2" color="text.secondary">
                  Urmatoarea data facturare: {formatIso(current.nextBillingDate)}
                </Typography>
              </Box>
            </>
          ) : null}

          <Divider sx={{ my: 2 }} />
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {canSubscribe ? (
              <Button variant="contained" onClick={openSubscribe} disabled={busy}>
                Alege abonament
              </Button>
            ) : null}
            {canOpenStripePortal ? (
              <Button variant="contained" color="secondary" onClick={() => void runOpenStripePortal()} disabled={busy}>
                Gestioneaza abonamentul
              </Button>
            ) : null}
            {canChange ? (
              <Button variant="outlined" onClick={openChange} disabled={busy}>
                Schimba planul
              </Button>
            ) : null}
            {canCancel ? (
              <Button color="warning" variant="outlined" onClick={() => setCancelDialog(true)} disabled={busy}>
                Anuleaza abonamentul
              </Button>
            ) : null}
            {canReactivate ? (
              <Button color="success" variant="outlined" onClick={() => void runReactivate()} disabled={busy}>
                Reactiveaza / revoca anularea programata
              </Button>
            ) : null}
            <Button variant="text" onClick={() => void load()} disabled={busy}>
              Reincarca
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {history?.subscriptions?.length ? (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Istoric abonamente
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Plan</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Perioada</TableCell>
                  <TableCell align="right">Suma</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.subscriptions.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell>{s.plan?.name || s.planName || s.planId}</TableCell>
                    <TableCell>{statusLabel(s.status, s.cancelAtPeriodEnd)}</TableCell>
                    <TableCell>
                      {formatIso(s.startDate)} – {formatIso(s.endDate)}
                    </TableCell>
                    <TableCell align="right">
                      {s.amount} {s.currency}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : null}

      {history?.events?.length ? (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Evenimente (audit operational)
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tip</TableCell>
                  <TableCell>Data</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.events.map((ev) => (
                  <TableRow key={ev.id}>
                    <TableCell>{ev.eventType}</TableCell>
                    <TableCell>{formatIso(ev.createdAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : null}

      <Dialog open={planDialog !== null} onClose={() => !busy && setPlanDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{planDialog === 'subscribe' ? 'Abonare clinica' : 'Schimbare plan'}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id="plan-sel">Plan</InputLabel>
            <Select
              labelId="plan-sel"
              label="Plan"
              value={selectedPlanId}
              onChange={(e) => {
                const id = String(e.target.value);
                setSelectedPlanId(id);
                const p = activePlans.find((x) => x.id === id);
                if (p && Number(p.price) > 0 && !p.allowManualClinicActivation) {
                  setBillingSubscribeMode('online');
                }
              }}
            >
              {activePlans.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name} — {p.price} {p.currency} / {p.billingCycle}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {planDialog === 'subscribe' ? (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="bill-sel">Mod plata</InputLabel>
              <Select
                labelId="bill-sel"
                label="Mod plata"
                value={billingSubscribeMode}
                onChange={(e) => setBillingSubscribeMode(e.target.value as 'manual' | 'online')}
              >
                <MenuItem value="online">Online — Stripe Checkout</MenuItem>
                <MenuItem value="manual">Manual (trial / plan permis / mediu de test)</MenuItem>
              </Select>
            </FormControl>
          ) : null}
          {planDialog === 'change' ? (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="eff-sel">Efect</InputLabel>
              <Select
                labelId="eff-sel"
                label="Efect"
                value={effective}
                onChange={(e) => setEffective(e.target.value as 'immediate' | 'period_end')}
              >
                <MenuItem value="immediate">Imediat</MenuItem>
                <MenuItem value="period_end">La sfarsitul perioadei curente (downgrade programat)</MenuItem>
              </Select>
            </FormControl>
          ) : null}
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
            {planDialog === 'subscribe' && billingSubscribeMode === 'online'
              ? 'Veti fi redirectionat catre Stripe. Abonamentul devine activ dupa plata reusita si webhook; pana atunci statusul ramane „in asteptare”.'
              : planDialog === 'subscribe'
                ? 'Activarea manuala este verificata pe server; pentru planuri platite in productie veti primi 403 daca nu este permis.'
                : 'Schimbarea imediata pentru abonamente Stripe actualizeaza pretul in Stripe. Downgrade-ul la sfarsitul perioadei este aplicat automat de job-ul serverului.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlanDialog(null)} disabled={busy}>
            Inchide
          </Button>
          <Button
            variant="contained"
            disabled={busy || !selectedPlanId}
            onClick={() => void (planDialog === 'subscribe' ? runSubscribe() : runChange())}
          >
            Confirma
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelDialog} onClose={() => !busy && setCancelDialog(false)}>
        <DialogTitle>Anulare abonament</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id="cancel-mode">Mod</InputLabel>
            <Select
              labelId="cancel-mode"
              label="Mod"
              value={cancelAtEnd ? 'end' : 'now'}
              onChange={(e) => setCancelAtEnd(e.target.value === 'end')}
            >
              <MenuItem value="end">La sfarsitul perioadei curente</MenuItem>
              <MenuItem value="now">Imediat</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Motiv (optional)"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialog(false)} disabled={busy}>
            Inchide
          </Button>
          <Button color="warning" variant="contained" disabled={busy} onClick={() => void runCancel()}>
            Confirma anularea
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Subscriptions;
